/**
 * Alert rules.
 *
 * This screen is the served-enums idea paying off. The event-type list, the
 * labels, the descriptions, and which per-type fields each rule needs all come
 * from `GET /api/v1/meta/enums` — the same table the server validates against
 * when a rule is saved. So this UI structurally cannot offer an event type
 * that would 400, and cannot show a threshold field for a type that ignores it.
 *
 * The Alpine app and mobile-ionic each keep their own hand-written copy of this
 * list, kept in step with the Rust const by a code comment asking a human to
 * remember. There is no copy here.
 *
 * Editable since the write endpoints were typed. Before that this screen could
 * list rules and not change them, which is worse than not showing them: a
 * setting you can read and not set looks broken rather than absent.
 */
import { useEffect, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, Group, Loader, Modal, MultiSelect,
  NumberInput, Select, Stack, Switch, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBellOff, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import {
  useAlerts, useCampaigns, useCreateAlert, useDeleteAlert, useEnums, useUpdateAlert,
} from '../api/queries';
import { formatDateTime } from '../theme';
import { useVocab, type Vocab } from '../vocab';

/**
 * Which leads a new-lead rule fires on.
 *
 * Only shown for `new_lead`, because it is the only event with a lead to have
 * a status. The wording says what each one costs you rather than naming the
 * stored value: "every lead" is the only one that includes duplicates, and a
 * duplicate is what a repeated test submission becomes.
 */
function newLeadScopes(v: Vocab) {
  return [
    { value: 'not_duplicate', label: `Every ${v.one('lead')} except duplicates` },
    { value: 'all', label: `Every ${v.one('lead')}, including duplicates` },
    { value: 'sold', label: `Only ${v.many('lead')} ${v.a('buyer')} took` },
  ];
}

type AlertRow = NonNullable<ReturnType<typeof useAlerts>['data']>['alerts'][number];

export function AlertsPage() {
  const alerts = useAlerts();
  const enums = useEnums();
  const campaigns = useCampaigns();
  const save = useUpdateAlert();
  const remove = useDeleteAlert();
  const v = useVocab();
  // 'new' opens the same form empty, with the event type to choose.
  const [editing, setEditing] = useState<AlertRow | 'new' | null>(null);

  const byValue = new Map(
    (enums.data?.alert_event_types ?? []).map((e) => [e.value, e]),
  );
  const campaignName = new Map(
    (campaigns.data?.campaigns ?? []).map((c) => [c.id, c.name]),
  );

  /** What this rule watches, phrased from the served metadata. */
  function summarise(a: NonNullable<typeof alerts.data>['alerts'][number]): string {
    const meta = byValue.get(a.event_type);
    if (!meta) return a.event_type;
    if (meta.has_threshold && meta.has_window) {
      return `${a.threshold_pct ?? 0}% failure over ${a.window_minutes ?? 60} min`;
    }
    if (meta.has_threshold) return `at ${a.threshold_pct ?? 0}% of cap`;
    if (meta.has_window) return `no ${v.many('lead')} for ${a.window_minutes ?? 60} min`;
    if (meta.has_revenue_target) {
      const t = a.threshold_count ?? 0;
      return t > 0 ? `£${t} today` : 'revenue target not set';
    }
    return meta.description.toLowerCase();
  }

  const loading = alerts.isPending || enums.isPending;
  const error = alerts.error ?? enums.error;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Alerts</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setEditing('new')}
          disabled={!enums.data}
        >
          New rule
        </Button>
      </Group>

      {error && <Alert color="red" variant="light">{error.message}</Alert>}

      {enums.data && (
        <Card>
          {/* The list comes from the server, so it always matches what the
              server will accept when a rule is saved. */}
          <Text fw={600} mb={4}>What you can be alerted about</Text>
          <Text size="sm" c="dimmed" mb="sm">
            Hover over a type to see when it fires.
          </Text>
          <Group gap="xs">
            {enums.data.alert_event_types.map((e) => (
              <Tooltip key={e.value} label={e.description} withArrow>
                <Badge variant="light" color="ink" style={{ cursor: 'help' }}>
                  {e.label}
                </Badge>
              </Tooltip>
            ))}
          </Group>
        </Card>
      )}

      <Card p={0}>
        {loading ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : alerts.data && alerts.data.alerts.length === 0 ? (
          <Stack align="center" py="xl" gap="xs">
            <IconBellOff size={28} opacity={0.4} />
            <Text fw={600}>No alert rules yet</Text>
            <Text size="sm" c="dimmed" maw={460} ta="center">
              {`Rules tell you when something needs a look, such as ${v.a('campaign')} going quiet or deliveries failing.`}
            </Text>
            <Button
              mt="xs"
              variant="default"
              leftSection={<IconPlus size={16} />}
              onClick={() => setEditing('new')}
              disabled={!enums.data}
            >
              Create a rule
            </Button>
          </Stack>
        ) : (
          <Table.ScrollContainer minWidth={820}>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Rule</Table.Th>
                  <Table.Th>Watches</Table.Th>
                  <Table.Th>Condition</Table.Th>
                  <Table.Th>{v.Many('campaign')}</Table.Th>
                  <Table.Th>Last fired</Table.Th>
                  <Table.Th>Enabled</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {alerts.data?.alerts.map((a) => {
                  const meta = byValue.get(a.event_type);
                  return (
                    <Table.Tr key={a.id}>
                      <Table.Td><Text size="sm" fw={500}>{a.name || '—'}</Text></Table.Td>
                      <Table.Td>
                        {meta ? (
                          <Tooltip label={meta.description} withArrow>
                            <Badge variant="light" color="ink" style={{ cursor: 'help' }}>
                              {meta.label}
                            </Badge>
                          </Tooltip>
                        ) : (
                          // Only reachable if a rule predates the current
                          // server, since saving validates against this list.
                          <Badge variant="light" color="chartRed">
                            {a.event_type} (unknown)
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{summarise(a)}</Text></Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {a.campaign_ids.length === 0
                            ? `All ${v.many('campaign')}`
                            : a.campaign_ids
                                .map((id) => campaignName.get(id) ?? id)
                                .join(', ')}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {a.last_fired_at ? formatDateTime(a.last_fired_at) : 'never'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {/* Saved on the spot. Enabling and disabling is the
                            thing done most often and least deserving of a
                            dialog — and a rule that is off is the one case
                            where seeing it and not being able to change it is
                            most annoying. */}
                        <Switch
                          checked={a.enabled}
                          aria-label={`${a.name} enabled`}
                          onChange={(e) =>
                            save.mutate(
                              { id: a.id, body: { enabled: e.currentTarget.checked } },
                              {
                                onError: (err) =>
                                  notifications.show({
                                    color: 'red',
                                    title: 'Could not change that',
                                    message: (err as Error).message,
                                  }),
                              },
                            )
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          <ActionIcon
                            variant="subtle"
                            aria-label={`Edit ${a.name}`}
                            onClick={() => setEditing(a)}
                          >
                            <IconPencil size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={`Delete ${a.name}`}
                            onClick={() => {
                              if (!window.confirm(`Delete "${a.name}"? This cannot be undone.`)) return;
                              remove.mutate(a.id, {
                                onError: (err) =>
                                  notifications.show({
                                    color: 'red',
                                    title: 'Could not delete that',
                                    message: (err as Error).message,
                                  }),
                              });
                            }}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      <AlertModal
        alert={editing}
        onClose={() => setEditing(null)}
        campaigns={campaigns.data?.campaigns ?? []}
        types={enums.data?.alert_event_types ?? []}
      />
    </Stack>
  );
}

type EventType = {
  value: string;
  label: string;
  description: string;
  has_threshold: boolean;
  has_window: boolean;
  has_revenue_target: boolean;
};

/*
  Starting values for a new rule, the same as the iOS app's, so a rule made on
  either one behaves the same.
*/
const NEW_RULE = { pct: 80, window: 60, count: 500 };

/**
 * Creating or editing one rule.
 *
 * Which fields appear comes from the served metadata, exactly as the table
 * above does it: a threshold box only for a type that has a threshold, a
 * window only for a type that has a window. So the form cannot offer a field
 * the engine would ignore, and cannot omit one it needs.
 *
 * The event type is chosen when a rule is created and fixed after that.
 * Changing it would quietly turn one rule into a different one with the old
 * rule's name and history.
 */
function AlertModal({
  alert,
  onClose,
  campaigns,
  types,
}: {
  alert: AlertRow | 'new' | null;
  onClose: () => void;
  campaigns: { id: string; name: string }[];
  types: EventType[];
}) {
  const save = useUpdateAlert();
  const create = useCreateAlert();
  const v = useVocab();
  const isNew = alert === 'new';
  const [eventType, setEventType] = useState('new_lead');
  const [name, setName] = useState('');
  // A new rule is named after its type until someone types a name of their own.
  const [nameEdited, setNameEdited] = useState(false);
  const [push, setPush] = useState(true);
  const [inApp, setInApp] = useState(true);
  const [pct, setPct] = useState<number | ''>('');
  const [count, setCount] = useState<number | ''>('');
  const [window, setWindow] = useState<number | ''>('');
  const [ids, setIds] = useState<string[]>([]);
  const [scope, setScope] = useState('not_duplicate');

  useEffect(() => {
    if (alert === null) return;
    if (alert === 'new') {
      const first = types.find((t) => t.value === 'new_lead') ?? types[0];
      setEventType(first?.value ?? 'new_lead');
      setName(first?.label ?? '');
      setNameEdited(false);
      setPush(true);
      setInApp(true);
      setPct(NEW_RULE.pct);
      setCount(NEW_RULE.count);
      setWindow(NEW_RULE.window);
      setIds([]);
      setScope('not_duplicate');
      return;
    }
    setEventType(alert.event_type);
    setName(alert.name);
    setNameEdited(true);
    setPush(alert.notify_push);
    setInApp(alert.notify_in_app);
    setPct(alert.threshold_pct ?? '');
    setCount(alert.threshold_count ?? '');
    setWindow(alert.window_minutes ?? '');
    setIds(alert.campaign_ids);
    setScope(alert.new_lead_scope || 'not_duplicate');
  }, [alert, types]);

  const meta = types.find((t) => t.value === eventType);

  function chooseType(value: string | null) {
    if (!value) return;
    setEventType(value);
    if (!nameEdited) setName(types.find((t) => t.value === value)?.label ?? '');
  }

  function submit() {
    if (alert === null) return;
    const finalName = name.trim() || meta?.label || eventType;
    // Sent only when the type uses them, so a rule cannot pick up a threshold
    // it ignores just by being opened and saved.
    const fields = {
      name: finalName,
      notify_push: push,
      notify_in_app: inApp,
      threshold_pct: meta?.has_threshold && pct !== '' ? Number(pct) : undefined,
      threshold_count: meta?.has_revenue_target && count !== '' ? Number(count) : undefined,
      window_minutes: meta?.has_window && window !== '' ? Number(window) : undefined,
      campaign_ids: ids,
      new_lead_scope: eventType === 'new_lead' ? scope : undefined,
    };
    const done = {
      onSuccess: () => {
        notifications.show({
          color: 'teal',
          title: isNew ? 'Rule created' : 'Alert saved',
          message: `${finalName} ${isNew ? 'is now on' : 'updated'}.`,
        });
        onClose();
      },
      onError: (e: unknown) =>
        notifications.show({
          color: 'red',
          title: 'Could not save that',
          message: (e as Error).message,
        }),
    };
    if (alert === 'new') {
      create.mutate({ ...fields, event_type: eventType, enabled: true }, done);
    } else {
      save.mutate({ id: alert.id, body: fields }, done);
    }
  }

  const title = alert === 'new' ? 'New alert rule' : `Edit ${alert?.name ?? 'alert'}`;

  return (
    <Modal opened={alert !== null} onClose={onClose} title={title} size="lg">
      {alert !== null && (
        <Stack>
          {isNew && (
            <Select
              label="Alert me when"
              description={meta?.description}
              data={types.map((t) => ({ value: t.value, label: t.label }))}
              value={eventType}
              onChange={chooseType}
              allowDeselect={false}
            />
          )}

          <TextInput
            label="Rule name"
            value={name}
            onChange={(e) => {
              setName(e.currentTarget.value);
              setNameEdited(true);
            }}
          />

          <MultiSelect
            label={v.Many('campaign')}
            description={`Leave empty to watch every ${v.one('campaign')}`}
            placeholder={ids.length === 0 ? `All ${v.many('campaign')}` : undefined}
            searchable
            clearable
            data={campaigns.map((c) => ({ value: c.id, label: c.name }))}
            value={ids}
            onChange={setIds}
          />

          {eventType === 'new_lead' && (
            <Select
              label={`Which ${v.many('lead')} count`}
              description={`A repeated test ${v.one('lead')} becomes a duplicate, so choose 'including duplicates' if you are testing.`}
              data={newLeadScopes(v)}
              value={scope}
              onChange={(value) => setScope(value ?? 'not_duplicate')}
              allowDeselect={false}
            />
          )}

          {meta?.has_threshold && (
            <NumberInput
              label="Threshold (%)"
              min={0}
              max={100}
              value={pct}
              onChange={(v) => setPct(v === '' ? '' : Number(v))}
            />
          )}
          {meta?.has_revenue_target && (
            <NumberInput
              label="Revenue target (£)"
              min={0}
              value={count}
              onChange={(v) => setCount(v === '' ? '' : Number(v))}
            />
          )}
          {meta?.has_window && (
            <NumberInput
              label="Window (minutes)"
              min={1}
              value={window}
              onChange={(v) => setWindow(v === '' ? '' : Number(v))}
            />
          )}

          <Group gap="lg">
            <Switch label="Push notification" checked={push} onChange={(e) => setPush(e.currentTarget.checked)} />
            <Switch label="Show in the app" checked={inApp} onChange={(e) => setInApp(e.currentTarget.checked)} />
          </Group>

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button loading={save.isPending || create.isPending} onClick={submit}>
              {isNew ? 'Create rule' : 'Save'}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
